import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import BrandBand from "@/components/BrandBand";
import Seo, { organizationLd, websiteLd } from "@/components/Seo";
import { getArticles, getCategories, getResources } from "@/lib/api";

const HERO_IMG = "https://customer-assets-4nw71qhi.emergentagent.net/job_sibo-hub/artifacts/h9a06o8h_pexels-vie-studio-7421115.webp";
const ROADMAP_IMG = "https://images.unsplash.com/photo-1601993957728-1e56ab70c5a8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxjYWxtJTIwbWluaW1hbCUyMGFyY2hpdGVjdHVyYWwlMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODI2MzM4NTV8MA&ixlib=rb-4.1.0&q=85";

const TESTIMONIALS = [
  { quote: "Finally, a resource that treats me like an adult. Measured, considered, and beautifully written.", who: "Reader, Portland" },
  { quote: "The starter guide changed the way I speak to my clinician. I feel prepared.", who: "Reader, London" },
  { quote: "Editorial in the truest sense. Serious information without the shouting.", who: "Reader, Melbourne" },
];

export default function Home() {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [resources, setResources] = useState([]);

  useEffect(() => {
    getArticles({ limit: 6 }).then(setArticles).catch(() => {});
    getCategories().then(setCategories).catch(() => {});
    getResources().then(setResources).catch(() => {});
  }, []);

  const featuredGuide = resources.find((r) => !r.premium) || resources[0];

  return (
    <div data-testid="home-page">
      <Seo
        title={null}
        description="The trusted editorial resource for Small Intestinal Bacterial Overgrowth. Evidence-based articles, clinician-reviewed protocols, and FAQs on SIBO symptoms, diagnosis, treatment, and diet."
        path="/"
        jsonLd={[organizationLd(), websiteLd()]}
        keywords="SIBO, Small Intestinal Bacterial Overgrowth, SIBO symptoms, SIBO treatment, low FODMAP, rifaximin, methane SIBO, IMO, IBS"
      />

      {/* HERO */}
      <section className="section" data-testid="hero-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6 md:col-start-1 animate-fade-up">
            <div className="overline">Understanding SIBO. Supporting Recovery.</div>
            <h1
              className="font-serif-editorial tight-track mt-8"
              style={{ fontSize: "clamp(3rem, 7vw, 6.5rem)", lineHeight: 0.95 }}
              data-testid="hero-title"
            >
              Your trusted<br/>resource for<br/>SIBO.
            </h1>
            <p className="mt-10 text-lg leading-relaxed" style={{ maxWidth: "32rem" }}>
              A dedicated resource for the education, support, and treatment of Small Intestinal Bacterial Overgrowth — education and empowerment for every step of the journey.
            </p>
            <div className="mt-12 flex flex-wrap gap-4">
              <Link to="/learn" className="btn-olive" data-testid="hero-start-learning">Start learning</Link>
              <Link to="/resources" className="btn-outline" data-testid="hero-view-resources">Free resources</Link>
            </div>
            <div className="mt-14 flex items-center gap-6 overline">
              <span>Educate</span>
              <span className="w-4 h-px" style={{ backgroundColor: "var(--sage)" }} />
              <span>Empower</span>
              <span className="w-4 h-px" style={{ backgroundColor: "var(--sage)" }} />
              <span>Heal</span>
            </div>
          </div>
          <div className="md:col-span-5 md:col-start-8 animate-fade-up">
            <Link to="/topic/what-is-sibo" className="block relative group" data-testid="hero-featured-link">
              <img src={HERO_IMG} alt="Editorial portrait" className="w-full h-[560px] object-cover transition-transform duration-700 group-hover:scale-[1.015]" style={{ filter: "saturate(0.92)", objectPosition: "center 92%" }} />
              <div className="absolute -bottom-6 -left-6 p-6 transition-colors duration-300 group-hover:bg-[var(--olive)] group-hover:text-[var(--cream)]" style={{ backgroundColor: "var(--sand)", maxWidth: "260px" }}>
                <div className="overline">Featured</div>
                <div className="font-serif-editorial mt-3 text-xl leading-tight">What is SIBO? Causes, symptoms, and solutions.</div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="section" style={{ backgroundColor: "var(--sand)" }} data-testid="categories-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between mb-16 flex-wrap gap-6">
            <div>
              <div className="overline">The library</div>
              <h2 className="font-serif-editorial tight-track mt-4" style={{ fontSize: "clamp(2.2rem, 4vw, 3.5rem)", lineHeight: 1 }}>
                Where to begin.
              </h2>
            </div>
            <Link to="/learn" className="editorial-link overline flex items-center gap-2" data-testid="view-all-categories">
              All categories <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16">
            {categories.map((c, i) => (
              <Link
                to={`/topic/${c.slug}`}
                key={c.slug}
                data-testid={`category-card-${c.slug}`}
                className="group"
              >
                <div className="aspect-[4/5] overflow-hidden">
                  <img src={c.image} alt={c.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="mt-6">
                  <div className="overline">{c.kicker}</div>
                  <div className="font-serif-editorial mt-3" style={{ fontSize: "1.9rem", lineHeight: 1.1 }}>{c.name}</div>
                  <div className="mt-3 text-sm opacity-80" style={{ maxWidth: "26rem" }}>{c.description}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TREATMENT ROADMAP */}
      <section className="section" data-testid="roadmap-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <img src={ROADMAP_IMG} alt="Steps forward" className="w-full h-[600px] object-cover" />
          </div>
          <div className="md:col-span-5 md:col-start-8">
            <div className="overline">A considered path</div>
            <h2 className="font-serif-editorial tight-track mt-6" style={{ fontSize: "clamp(2rem, 3.5vw, 3rem)", lineHeight: 1.05 }}>
              The treatment roadmap.
            </h2>
            <p className="mt-8 text-lg">
              Recovery from SIBO is rarely a single act. It is a sequence — reduce, restore, rebuild — carried out with patience and evidence.
            </p>
            <div className="mt-10 space-y-6">
              {[
                { n: "01", t: "Reduce", d: "Antimicrobial protocols — pharmaceutical or herbal — lower the bacterial burden." },
                { n: "02", t: "Restore", d: "Prokinetic support and root-cause work to prevent recurrence." },
                { n: "03", t: "Rebuild", d: "A slow reintroduction of foods and restoration of a diverse microbiome." },
              ].map((s) => (
                <div key={s.n} className="flex gap-6 items-start" data-testid={`roadmap-step-${s.n}`}>
                  <div className="font-serif-editorial" style={{ fontSize: "2.4rem", color: "var(--sage)", lineHeight: 1 }}>{s.n}</div>
                  <div>
                    <div className="font-serif-editorial text-xl">{s.t}</div>
                    <div className="text-sm mt-1 opacity-80">{s.d}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-12">
              <Link to="/topic/treatment" className="btn-outline" data-testid="roadmap-cta">Read the full roadmap</Link>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED GUIDE (replacing product spotlight per user's request) */}
      {featuredGuide && (
        <section className="section" style={{ backgroundColor: "var(--sand)" }} data-testid="featured-guide-section">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-5">
              <div className="overline">Featured guide</div>
              <h2 className="font-serif-editorial tight-track mt-6" style={{ fontSize: "clamp(2.2rem, 4vw, 3.5rem)", lineHeight: 1 }}>
                {featuredGuide.title}
              </h2>
              <p className="mt-8 text-lg" style={{ maxWidth: "28rem" }}>{featuredGuide.description}</p>
              <div className="mt-10 flex gap-4">
                <Link to={`/resources/${featuredGuide.slug}`} className="btn-olive" data-testid="featured-guide-cta">
                  {featuredGuide.premium ? "Purchase & download" : "Get the free guide"}
                </Link>
              </div>
            </div>
            <div className="md:col-span-6 md:col-start-7">
              <div className="aspect-[4/5] max-w-md mx-auto md:ml-auto" style={{ backgroundColor: "var(--cream)" }}>
                <img src={featuredGuide.image} alt={featuredGuide.title} className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* LATEST ARTICLES */}
      <section className="section" data-testid="latest-articles-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between mb-16 flex-wrap gap-6">
            <div>
              <div className="overline">Editorial</div>
              <h2 className="font-serif-editorial tight-track mt-4" style={{ fontSize: "clamp(2.2rem, 4vw, 3.5rem)", lineHeight: 1 }}>
                Latest reading.
              </h2>
            </div>
            <Link to="/learn" className="editorial-link overline flex items-center gap-2" data-testid="view-all-articles">
              All articles <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16">
            {articles.slice(0, 6).map((a) => (
              <Link to={`/article/${a.slug}`} key={a.slug} className="group" data-testid={`article-card-${a.slug}`}>
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={a.image} alt={a.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="mt-6">
                  <div className="overline">{a.kicker} · {a.reading_time}</div>
                  <div className="font-serif-editorial mt-3" style={{ fontSize: "1.6rem", lineHeight: 1.15 }}>{a.title}</div>
                  <div className="mt-3 text-sm opacity-80">{a.excerpt}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section" style={{ backgroundColor: "var(--sand)" }} data-testid="testimonials-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="overline">From the community</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mt-16">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} data-testid={`testimonial-${i}`}>
                <div className="font-serif-editorial" style={{ fontSize: "1.6rem", lineHeight: 1.35 }}>&ldquo;{t.quote}&rdquo;</div>
                <div className="mt-6 overline">{t.who}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BrandBand />
    </div>
  );
}
